import {
  clearPendingCatalogSyncAfterUpdate,
  emitCatalogSyncBusy,
  emitCatalogSyncIdle,
  isPendingCatalogSyncAfterUpdate,
} from '@core/domain/sessionEvents.ts';
import type { SyncPushProgress } from '@core/domain/sync/SyncPushResult.ts';
import type { NotificationPort } from '@src/modules/platform/notifications/domain/ports/NotificationPort.ts';
import type { SystemApplicationService } from '@src/modules/system/application/SystemApplicationService.ts';
import { formatPullCatalogMessage } from '@core/bootstrap/shell/pages/system/SyncPageComponent.ts';
import type { EntregaCatalogRepositoryPort } from '@src/modules/entrega-paquetes/domain/ports/EntregaPorts.ts';
import { warmupShellOfflineAssets } from '@core/bootstrap/factories/offlineAssetWarmup.ts';
import { probeAppNetworkReachable } from '@core/bootstrap/factories/networkReachabilityProbe.ts';
import {
  CATALOG_SYNC_OVERLAY_ID,
  hideCatalogSyncOverlay,
  morphCatalogSyncOverlayToPush,
  morphCatalogSyncOverlayToWarmup,
  showCatalogSyncOverlay,
  updateCatalogSyncPullOverlay,
  updateCatalogSyncPushOverlay,
  updateCatalogSyncWarmupOverlay,
} from '@src/modules/shell-ui/infrastructure/shellSyncOverlay.ts';

export { CATALOG_SYNC_OVERLAY_ID };

let postLoginCatalogSync: Promise<void> | null = null;
let postLoginCatalogSyncPullOnly = false;
let catalogSyncActive = false;

/** True while a catalog sync run holds the global busy overlay. */
export function isCatalogSyncActive(): boolean {
  return catalogSyncActive;
}

/** Pre-login pull on `/login` (always; still needs network when strict post-login runs). */
export function shouldRunCatalogSyncOnLoginPage(): boolean {
  return true;
}

export type PostLoginCatalogSyncOptions = {
  /** Default true; false on session restore / reconnect to avoid noisy offline toasts. */
  warnIfOffline?: boolean;
  /** Default true on login: block entry when offline, sync fails, or warmup fails. */
  blockOnWarmupFailure?: boolean;
  /** Pre-login: pull catalogs only; skip push of pending local records. */
  pullOnly?: boolean;
  entregaCatalog?: EntregaCatalogRepositoryPort;
};

function handlePostLoginPullProgress(progress: Parameters<typeof updateCatalogSyncPullOverlay>[0]): void {
  updateCatalogSyncPullOverlay(progress);
}

function handlePostLoginPushProgress(progress: SyncPushProgress): void {
  updateCatalogSyncPushOverlay(progress);
}

function finishPostLoginCatalogSyncRun(): void {
  catalogSyncActive = false;
  hideCatalogSyncOverlay();
  emitCatalogSyncIdle();
  clearPendingCatalogSyncAfterUpdate();
  postLoginCatalogSync = null;
  postLoginCatalogSyncPullOnly = false;
}

/** Pull catalogs (merge by default; repopulate after app update) + push; deduped + overlay. */
export function runPostLoginCatalogSync(
  system: SystemApplicationService,
  notifications?: NotificationPort,
  opts?: PostLoginCatalogSyncOptions,
): Promise<void> {
  const pullOnly = opts?.pullOnly === true;
  if (postLoginCatalogSync) {
    if (!pullOnly && postLoginCatalogSyncPullOnly) {
      const prev = postLoginCatalogSync;
      postLoginCatalogSync = null;
      postLoginCatalogSyncPullOnly = false;
      return prev.catch(() => {}).then(() => runPostLoginCatalogSync(system, notifications, opts));
    }
    return postLoginCatalogSync;
  }

  postLoginCatalogSyncPullOnly = pullOnly;
  postLoginCatalogSync = (async () => {
    const strict = opts?.blockOnWarmupFailure !== false;
    let catalogSyncOk = false;
    let syncErrorNotified = false;
    let exitError: unknown;
    catalogSyncActive = true;
    showCatalogSyncOverlay();
    emitCatalogSyncBusy();
    notifications?.info('Sincronizando catálogos…');
    try {
      if (globalThis.navigator?.onLine === false) {
        const offlineMsg = 'Sin conexión: conéctese a internet para iniciar sesión.';
        if (opts?.warnIfOffline !== false) {
          notifications?.warning(offlineMsg);
        }
        if (strict) throw new Error(offlineMsg);
        return;
      }
      if (strict && !(await probeAppNetworkReachable())) {
        const unreachableMsg = 'Sin conexión usable: no se pudo contactar el servidor.';
        if (opts?.warnIfOffline !== false) {
          notifications?.warning(unreachableMsg);
        }
        throw new Error(unreachableMsg);
      }
      const repopulate = isPendingCatalogSyncAfterUpdate();
      let mergedCount = 0;
      let pullError: string | null = null;
      let pushedEntregas = 0;
      let pushedReposiciones = 0;
      if (opts?.pullOnly) {
        try {
          ({ mergedCount } = await system.pullNow({
            repopulate,
            onDownloadProgress: handlePostLoginPullProgress,
          }));
        } catch (e) {
          pullError = e instanceof Error ? e.message : String(e);
        }
      } else {
        ({ mergedCount, pullError, pushedEntregas, pushedReposiciones } = await system.syncAll({
          repopulate,
          onDownloadProgress: handlePostLoginPullProgress,
          onPushStart: () => morphCatalogSyncOverlayToPush({ processed: 0, total: 1 }),
          onPushProgress: handlePostLoginPushProgress,
        }));
      }
      const msg = formatPullCatalogMessage(pullError, mergedCount);
      const pushed = pushedEntregas + pushedReposiciones;
      const full = pushed > 0 ? `${msg} ${pushed} registro(s) enviado(s).` : msg;
      if (pullError) {
        notifications?.error(full);
        syncErrorNotified = true;
        if (strict) throw new Error(full);
        return;
      }
      notifications?.info(full);
      catalogSyncOk = true;
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e);
      const offlineBlock = strict && errMsg.startsWith('Sin conexión');
      if (!offlineBlock && !syncErrorNotified) {
        notifications?.error(`No se pudieron descargar catálogos: ${errMsg}`);
        // oxlint-disable-next-line no-console -- login bootstrap fallback
        console.warn('[sync] post-login catalog sync failed', e);
      }
      if (strict) exitError = e;
    }

    if (exitError === undefined && globalThis.navigator?.onLine !== false && catalogSyncOk) {
      try {
        morphCatalogSyncOverlayToWarmup();
        await warmupShellOfflineAssets({
          entregaCatalog: opts?.entregaCatalog,
          onMapProgress: (processed, total) => updateCatalogSyncWarmupOverlay({ processed, total }),
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        notifications?.error(`No se pudo preparar modo offline: ${msg}`);
        // oxlint-disable-next-line no-console -- login bootstrap fallback
        console.warn('[sync] post-login offline warmup failed', e);
        if (strict) exitError = e;
      }
    }

    finishPostLoginCatalogSyncRun();
    if (exitError !== undefined) throw exitError;
  })();

  return postLoginCatalogSync;
}

/** @internal test hook */
export function resetPostLoginCatalogSyncForTests(): void {
  postLoginCatalogSync = null;
  postLoginCatalogSyncPullOnly = false;
  catalogSyncActive = false;
  hideCatalogSyncOverlay();
}
